import { z } from 'zod';
import { diffDays, isISODate } from './dates';
import { BILLING_TYPES } from './types';

/** Input building blocks shared by the server procedures and the web forms. */

export const zId = z.string().regex(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i, 'invalid id');
/**
 * Years the platform works with (bookings, contracts since the entry date, plans, reports). A date outside is a typing
 * error; it must not reach the day-by-day computations either.
 */
export const MIN_YEAR = 1950;
export const MAX_YEAR = 2100;
export const zDate = z.string().refine((v) => isISODate(v) && Number(v.slice(0, 4)) >= MIN_YEAR && Number(v.slice(0, 4)) <= MAX_YEAR, 'invalid date');

/**
 * Period of at most `maxDays` days (from <= to). Every report and every computation that runs day by day takes its
 * period through this, so one request cannot ask for centuries of work.
 */
export function zPeriod(maxDays: number) {
  return limitPeriod(z.object({ from: zDate, to: zDate }), maxDays);
}

/** Adds the checks of a period to an object schema with from/to (both optional: checked when both are given). */
export function limitPeriod<S extends z.ZodType<{ from?: string | null; to?: string | null }>>(schema: S, maxDays: number): S {
  return schema
    .refine((r) => !r.from || !r.to || r.from <= r.to, { message: 'from after to', path: ['to'] })
    .refine((r) => !r.from || !r.to || diffDays(r.to, r.from) < maxDays, { message: `period longer than ${maxDays} days`, path: ['to'] });
}

/** Limits of periods (days) per kind of request. */
export const PERIOD_LIMITS = {
  /** key figures, cockpit, workload: computed day by day */
  figures: 731,
  /** workload planning and allocations: week by week */
  planning: 371,
  /** lists and exports of entries and absences: paged, but bounded */
  lists: 3660,
  /** one absence (parental leave can last three years) */
  absence: 1100,
} as const;
export const zMinutes = z.number().int().min(1).max(24 * 60);
export const zBillingType = z.enum(BILLING_TYPES);
export const zReason = z.string().trim().min(3).max(500);
export const zOptReason = z.string().trim().max(500).optional();

/**
 * Hours to invoice for one entry. Management may invoice more (or less) than was recorded,
 * e.g. value based billing; recorded minutes stay unchanged and are used for all other analyses.
 */
export const MAX_BILLED_MINUTES = 100 * 60;
export const zBilledMinutes = z.number().int().min(0).max(MAX_BILLED_MINUTES);

export const zRange = z
  .object({ from: zDate, to: zDate })
  .refine((r) => r.from <= r.to, 'from must be before to');

export const entryCreateSchema = z.object({
  taskId: zId,
  workDate: zDate,
  minutes: zMinutes,
  description: z.string().trim().max(2000).default(''),
  billingType: zBillingType.optional(),
  workLocation: z.string().trim().max(120).nullish(),
  /** Managers may book for another person. */
  personId: zId.optional(),
  /** "Zählt nicht als Überstunden" (setting time.noOvertime) */
  noOvertime: z.boolean().optional(),
});
export type EntryCreateInput = z.input<typeof entryCreateSchema>;

export const entryUpdateSchema = z.object({
  id: zId,
  version: z.number().int().min(1),
  taskId: zId.optional(),
  workDate: zDate.optional(),
  minutes: zMinutes.optional(),
  description: z.string().trim().max(2000).optional(),
  billingType: zBillingType.optional(),
  workLocation: z.string().trim().max(120).nullish(),
  billedMinutes: zBilledMinutes.nullish(),
  billingNote: z.string().trim().max(500).nullish(),
  noOvertime: z.boolean().optional(),
  reason: zOptReason,
});
export type EntryUpdateInput = z.input<typeof entryUpdateSchema>;

export const manualTaskSchema = z.object({
  projectId: zId,
  title: z.string().trim().min(2).max(200),
  description: z.string().trim().max(2000).optional(),
  category: z.string().trim().min(1).max(60),
  defaultBillingType: zBillingType,
  billingTypeFixed: z.boolean().default(false),
  /** Planned hours (only project leads and management; ignored otherwise). */
  plannedMinutes: z.number().int().min(0).max(100_000).nullish(),
  /** Start and due date (timeline and schedule state of the analysis). */
  startDate: zDate.nullish(),
  dueDate: zDate.nullish(),
});
export type ManualTaskInput = z.input<typeof manualTaskSchema>;
